import { useEffect } from 'react';

/**
 * Hook to register a global keyboard shortcut
 * @param key - The key to listen for (e.g., 'k')
 * @param modifiers - Required modifier keys
 * @param callback - Function to call when shortcut is triggered
 */
export function useGlobalShortcut(
  key: string,
  modifiers: { meta?: boolean; ctrl?: boolean; shift?: boolean; alt?: boolean },
  callback: () => void,
) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        activeElement?.getAttribute('contenteditable') === 'true'
      ) {
        return;
      }

      const keyMatches = event.key.toLowerCase() === key.toLowerCase();
      const metaMatches = modifiers.meta ? event.metaKey : !event.metaKey;
      const ctrlMatches = modifiers.ctrl ? event.ctrlKey : !event.ctrlKey;
      const shiftMatches = modifiers.shift ? event.shiftKey : !event.shiftKey;
      const altMatches = modifiers.alt ? event.altKey : !event.altKey;

      // For cross-platform, treat Cmd (meta) and Ctrl as interchangeable if either is specified
      const modifierMatches =
        modifiers.meta || modifiers.ctrl
          ? (event.metaKey || event.ctrlKey) && shiftMatches && altMatches
          : metaMatches && ctrlMatches && shiftMatches && altMatches;

      if (keyMatches && modifierMatches) {
        event.preventDefault();
        callback();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [key, modifiers, callback]);
}
